import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import axios from '../../../lib/axios.js';
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Building2 } from "lucide-react";
import CallsTab from "./CallsTab.jsx";
import { Button } from "@/components/ui/button.jsx";
import { GearIcon } from "@radix-ui/react-icons";
import SecretShopper from "./SecretShoppersTab.jsx";
import Emails from "../Emails.jsx";
import { Skeleton } from "@/components/ui/skeleton.jsx";

export default function CommunityPage() {
    const { id: communityId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [community, setCommunity] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();

    const defaultTab = community?.status === 'secret-shopper' ? 'secret-shopper' : 'calls';
    const tabParam = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(tabParam || defaultTab);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const communityResponse = await axios.get(`/communities/${communityId}`);
                setCommunity(communityResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load community details. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [communityId, toast]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const param = searchParams.get('tab');

        if (community?.status === 'secret-shopper') {
            searchParams.delete('tab');
            setSearchParams(searchParams, { replace: true });
            setActiveTab('secret-shopper');
            return;
        }
        
        setActiveTab(param || 'calls');
    }, [isLoading, community?.status, searchParams, setSearchParams]);

    const handleTabChange = (value) => {
        if (value === defaultTab) {
            searchParams.delete('tab');
        } else {
            searchParams.set('tab', value);
        }
        setSearchParams(searchParams);
        setActiveTab(value);
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-7xl mx-auto mt-6 space-y-6">
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <Skeleton className="h-8 w-8 rounded-md" />
                        <Skeleton className="h-8 w-48" />
                    </div>
                    <Skeleton className="h-10 w-24 rounded-md" />
                </div>

                <div className="space-y-2">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Skeleton className="h-40 w-full" />
                        <Skeleton className="h-40 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    return (

        <div className="w-full max-w-7xl mx-auto">
            <div className="sm:flex justify-between">
                <div className="flex items-center space-x-3">
                    <Building2 className="h-8 w-8 text-primary" />
                    <h1 className="text-3xl font-medium">{community?.community_name}</h1>
                </div>

                <div className="flex">
                    <Link to={`/communities/${communityId}/settings`}>
                        <Button variant="ghost" className="text-lg flex items-center gap-2 p-0">
                            <GearIcon className="h-5 w-5" />
                            Settings
                        </Button>
                    </Link>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-6">
                {community?.status !== 'secret-shopper' && <TabsList className="mb-6">
                    <TabsTrigger value="calls">Calls</TabsTrigger>
                    <TabsTrigger value="emails">Emails</TabsTrigger>
                    <TabsTrigger value="secret-shopper">Secret Shopper</TabsTrigger>
                </TabsList>}

                {community?.id && <CallsTab community={community} />}

                {community?.id && <SecretShopper community={community} />}
                {community?.id && <Emails community={community} />}
            </Tabs>
        </div>
    );
}
